import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "../auth/supabaseClient";
import {
  hallPatrolQuickActions,
  type HallPatrolAction,
  type HallPatrolTankSnapshot,
} from "../qr/hallPatrol";
import type { OutboxSummary } from "../sync/types";
import { BreedingActionPanel } from "./BreedingActionPanel";
import { BreedingManagementPanel, type BreedingManagerAction } from "./BreedingManagementPanel";
import { playBundledPersianAudio, speakPersian } from "./audioGuidance";
import {
  cycleHistoryForPair,
  loadBreedingInventorySnapshot,
  readBreedingInventoryCache,
  writeBreedingInventoryCache,
  type BreedingInventorySnapshot,
} from "../breeding/liveBreedingInventory";
import { destinationTankIsEmpty } from "../breeding/pairFormationFromStock";
import { PairFormationFromStockPanel } from "./PairFormationFromStockPanel";
import { PairMovePanel } from "./PairMovePanel";

interface BreedingInventoryPanelProps {
  farmId: string;
  userId: string;
  selectedTankId?: string;
  hideTankSelector?: boolean;
  onSyncSummary?: (summary: OutboxSummary) => void;
}

function sexLabel(value: string): string {
  if (value === "male") return "نر";
  if (value === "female") return "ماده";
  return "نامشخص";
}

function breederStatusLabel(value: string): string {
  if (value === "active") return "فعال";
  if (value === "retired") return "بازنشسته";
  if (value === "deceased") return "تلف‌شده";
  return "خارج‌شده";
}

function cycleStateLabel(value: string): string {
  const labels: Record<string, string> = {
    eggs_pending: "تخم",
    hatched: "هچ",
    free_swim: "شنای آزاد",
    parent_feeding: "تغذیه از والدین",
    separated: "جداسازی",
    growout: "گله رشد",
    failed: "ناموفق",
    completed: "کامل",
  };
  return labels[value] ?? value;
}

export function BreedingInventoryPanel({
  farmId,
  userId,
  selectedTankId: externalSelectedTankId,
  hideTankSelector = false,
  onSyncSummary,
}: BreedingInventoryPanelProps) {
  const [snapshot, setSnapshot] = useState<BreedingInventorySnapshot>();
  const [source, setSource] = useState<"live" | "cache" | "empty">("empty");
  const [selectedTankId, setSelectedTankId] = useState("");
  const [lifecycleAction, setLifecycleAction] = useState<HallPatrolAction>();
  const [managementAction, setManagementAction] = useState<BreedingManagerAction>();
  const [pairFormationOpen, setPairFormationOpen] = useState(false);
  const [pairMoveOpen, setPairMoveOpen] = useState(false);
  const [error, setError] = useState<string>();

  const refresh = useCallback(async () => {
    setError(undefined);

    if (typeof navigator !== "undefined" && navigator.onLine === false) {
      const cached = readBreedingInventoryCache(farmId);
      setSnapshot(cached);
      setSource(cached ? "cache" : "empty");
      return;
    }

    try {
      const live = await loadBreedingInventorySnapshot(supabase, { farmId });
      writeBreedingInventoryCache(live);
      setSnapshot(live);
      setSource("live");
    } catch (caught) {
      const cached = readBreedingInventoryCache(farmId);
      if (cached) {
        setSnapshot(cached);
        setSource("cache");
        setError("به‌روزرسانی آنلاین انجام نشد؛ آخرین snapshot محلی نمایش داده می‌شود.");
      } else {
        setSnapshot(undefined);
        setSource("empty");
        setError(caught instanceof Error ? caught.message : "بارگذاری اطلاعات تکثیر انجام نشد.");
      }
    }
  }, [farmId]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const activeTanks = useMemo(
    () => snapshot?.tanks.filter((tank) => tank.active) ?? [],
    [snapshot],
  );
  useEffect(() => {
    if (externalSelectedTankId && selectedTankId !== externalSelectedTankId) {
      setSelectedTankId(externalSelectedTankId);
      setLifecycleAction(undefined);
      setManagementAction(undefined);
      setPairFormationOpen(false);
      setPairMoveOpen(false);
      setError(undefined);
      return;
    }

    if (!externalSelectedTankId && !selectedTankId && activeTanks[0]) {
      setSelectedTankId(activeTanks[0].id);
    }
  }, [activeTanks, externalSelectedTankId, selectedTankId]);

  if (!snapshot) {
    return (
      <section className="section-card">
        <p className="section-kicker">تکثیر</p>
        <h2>اطلاعات تکثیر در دسترس نیست</h2>
        {error ? <p className="settings-warning">{error}</p> : null}
        <button type="button" className="secondary-button" onClick={() => void refresh()}>
          تلاش مجدد
        </button>
      </section>
    );
  }

  const selectedTank = snapshot.tanks.find((tank) => tank.id === selectedTankId);
  const tankOccupancies = snapshot.occupancies.filter(
    (occupancy) => occupancy.tankId === selectedTankId,
  );
  const pairOccupancy = tankOccupancies.find(
    (occupancy) => occupancy.occupantKind === "pair" && occupancy.pairId,
  );
  const breederOccupancy = tankOccupancies.find(
    (occupancy) => occupancy.occupantKind === "breeder" && occupancy.breederId,
  );
  const selectedPair = pairOccupancy?.pairId
    ? snapshot.pairs.find((pair) => pair.id === pairOccupancy.pairId)
    : undefined;
  const selectedBreeder = breederOccupancy?.breederId
    ? snapshot.breeders.find((breeder) => breeder.id === breederOccupancy.breederId)
    : undefined;
  const stockOccupancy = tankOccupancies.find(
    (occupancy) => occupancy.occupantKind === "stock_group" && occupancy.stockGroupId,
  );
  const selectedStock = stockOccupancy?.stockGroupId
    ? snapshot.stockGroups.find((stock) => stock.id === stockOccupancy.stockGroupId)
    : undefined;
  const pairCycles = selectedPair ? cycleHistoryForPair(snapshot.cycles, selectedPair.id) : [];
  const male = selectedPair
    ? snapshot.breeders.find((breeder) => breeder.id === selectedPair.maleBreederId)
    : undefined;
  const female = selectedPair
    ? snapshot.breeders.find((breeder) => breeder.id === selectedPair.femaleBreederId)
    : undefined;
  const pairTank = selectedPair ? selectedTank : undefined;
  const activeCycle = pairCycles.find(
    (cycle) => cycle.state !== "completed" && cycle.state !== "failed",
  );
  const lifecycleTank: HallPatrolTankSnapshot | undefined =
    selectedPair?.status === "active" && pairTank
      ? {
          farmId,
          tankId: pairTank.id,
          tankCode: pairTank.tankCode,
          active: pairTank.active,
          permissions: ["breeding.record"],
          breeding: {
            pairId: selectedPair.id,
            pairVersion: selectedPair.domainVersion,
            ...(activeCycle
              ? {
                  cycleId: activeCycle.id,
                  cycleVersion: activeCycle.domainVersion,
                  cycleState: activeCycle.state,
                  ...(activeCycle.separatedAt ? { separatedAt: activeCycle.separatedAt } : {}),
                }
              : {}),
          },
        }
      : undefined;
  const lifecycleActions = lifecycleTank
    ? hallPatrolQuickActions(lifecycleTank).filter((item) =>
        item.operationType.startsWith("breeding."),
      )
    : [];

  const separationAction =
    lifecycleActions.find((item) => item.action === "parent_separation_record")?.action ??
    lifecycleActions.find((item) => item.action === "growout_record")?.action;

  function lifecycleAllowed(action: HallPatrolAction): boolean {
    return lifecycleActions.some((item) => item.action === action);
  }

  if (pairFormationOpen && selectedTank) {
    return (
      <section className="section-card" aria-labelledby="pair-formation-title">
        <div className="section-title-row">
          <h2 id="pair-formation-title">تشکیل جفت</h2>
        </div>
        <button
          type="button"
          className="secondary-button large-button"
          onClick={() => {
            setPairFormationOpen(false);
            speakPersian("بازگشت به منوی تکثیر");
          }}
        >
          ↩️ بازگشت به تکثیر
        </button>
        <PairFormationFromStockPanel
          farmId={farmId}
          userId={userId}
          snapshot={snapshot}
          destinationTankId={selectedTank.id}
          {...(onSyncSummary ? { onSyncSummary } : {})}
          onCompleted={() => {
            setPairFormationOpen(false);
            void refresh();
          }}
        />
      </section>
    );
  }

  if (pairMoveOpen && selectedPair) {
    return (
      <section className="section-card" aria-labelledby="pair-move-title">
        <div className="section-title-row">
          <h2 id="pair-move-title">جابجایی جفت</h2>
        </div>
        <button
          type="button"
          className="secondary-button large-button"
          onClick={() => {
            setPairMoveOpen(false);
            speakPersian("بازگشت به منوی تکثیر");
          }}
        >
          ↩️ بازگشت به تکثیر
        </button>
        <PairMovePanel
          farmId={farmId}
          userId={userId}
          snapshot={snapshot}
          pairId={selectedPair.id}
          {...(onSyncSummary ? { onSyncSummary } : {})}
          onCompleted={() => {
            setPairMoveOpen(false);
            void refresh();
          }}
        />
      </section>
    );
  }

  if (managementAction) {
    const labels: Partial<Record<BreedingManagerAction, string>> = {
      pair_form: "تشکیل جفت",
      pair_move: "جابجایی جفت",
      pair_exit: "خروج جفت از مجموعه",
    };
    return (
      <section className="section-card" aria-labelledby="breeding-management-title">
        <div className="section-title-row">
          <h2 id="breeding-management-title">{labels[managementAction] ?? "مدیریت تکثیر"}</h2>
        </div>
        <button
          type="button"
          className="secondary-button large-button"
          onClick={() => {
            setManagementAction(undefined);
            speakPersian("بازگشت به منوی تکثیر");
          }}
        >
          ↩️ بازگشت به تکثیر
        </button>
        <BreedingManagementPanel
          farmId={farmId}
          userId={userId}
          snapshot={snapshot}
          initialAction={managementAction}
          showActionPicker={false}
          {...(selectedPair ? { preferredPairId: selectedPair.id } : {})}
          {...(managementAction === "pair_form" && selectedTank
            ? { preferredTargetTankId: selectedTank.id }
            : {})}
          {...(onSyncSummary ? { onSyncSummary } : {})}
          onCompleted={() => {
            setManagementAction(undefined);
            void refresh();
          }}
        />
      </section>
    );
  }

  return (
    <section className="section-card breeding-quick-menu" aria-labelledby="breeding-title">
      <div className="section-title-row">
        <h2 id="breeding-title">تکثیر</h2>
        <span className="muted-chip">{source === "live" ? "● آنلاین" : "● آفلاین"}</span>
      </div>

      {error ? <p className="settings-warning">{error}</p> : null}

      {!hideTankSelector ? (
        <label className="operation-field">
          <span>تانک</span>
          <select
            value={selectedTankId}
            onChange={(event) => {
              const tankId = event.target.value;
              setSelectedTankId(tankId);
              setLifecycleAction(undefined);
              setPairFormationOpen(false);
              setPairMoveOpen(false);
              setError(undefined);
              const tank = activeTanks.find((candidate) => candidate.id === tankId);
              if (tank) speakPersian(`تانک ${tank.tankCode} انتخاب شد`);
            }}
          >
            {activeTanks.map((tank) => (
              <option value={tank.id} key={tank.id}>
                {tank.tankCode}
              </option>
            ))}
          </select>
        </label>
      ) : null}

      <article className="manager-card breeding-tank-summary">
        <strong>{selectedTank ? `تانک ${selectedTank.tankCode}` : "تانک انتخاب نشده"}</strong>
        {selectedPair ? (
          <p>
            💞 {selectedPair.pairCode} · نر {male?.breederCode ?? "نامشخص"} · ماده{" "}
            {female?.breederCode ?? "نامشخص"}
            {activeCycle ? ` · ${cycleStateLabel(activeCycle.state)}` : ""}
          </p>
        ) : selectedBreeder ? (
          <p>
            🐟 مولد {selectedBreeder.breederCode} · {sexLabel(selectedBreeder.sex)} ·{" "}
            {breederStatusLabel(selectedBreeder.status)}
          </p>
        ) : selectedStock ? (
          <p>
            🐠 این تانک خالی نیست · گله {selectedStock.groupCode ?? "فعال"}
            {selectedStock.quantity === undefined
              ? " · تعداد نامشخص"
              : ` · ${selectedStock.quantity.toLocaleString("fa-IR")} عدد`}
          </p>
        ) : tankOccupancies.length > 0 ? (
          <p>این تانک خالی نیست.</p>
        ) : (
          <p>✅ این تانک خالی است و برای تشکیل جفت آماده است.</p>
        )}
      </article>

      <button
        type="button"
        className="secondary-button"
        onClick={() => {
          setError(undefined);
          void playBundledPersianAudio("/audio/breeding/menu.mp3").then((played) => {
            if (!played) {
              setError("فایل صوتی داخلی برنامه پخش نشد. لطفاً همین مورد را گزارش کنید.");
            }
          });
        }}
      >
        🔊 راهنمای صوتی
      </button>

      <div className="quick-grid breeding-v1-grid">
        <button
          type="button"
          className="quick-button"
          disabled={!selectedTank}
          onClick={() => {
            if (!selectedTank) return;
            if (!destinationTankIsEmpty(snapshot, selectedTank.id)) {
              const message = `تانک ${selectedTank.tankCode} خالی نیست و امکان تشکیل جفت جدید ندارد.`;
              setError(message);
              speakPersian(message);
              return;
            }
            setError(undefined);
            setPairFormationOpen(true);
            void playBundledPersianAudio("/audio/breeding/pair-formation.mp3");
          }}
        >
          <span aria-hidden="true">➕</span>
          <strong>تشکیل جفت</strong>
        </button>

        <button
          type="button"
          className="quick-button"
          disabled={!selectedPair}
          onClick={() => {
            setPairMoveOpen(true);
            void playBundledPersianAudio("/audio/breeding/pair-move.mp3");
          }}
        >
          <span aria-hidden="true">↔️</span>
          <strong>جابجایی جفت</strong>
        </button>

        <button
          type="button"
          className={lifecycleAction === "spawn_record" ? "quick-button active" : "quick-button"}
          disabled={!lifecycleAllowed("spawn_record")}
          onClick={() => {
            setLifecycleAction("spawn_record");
            void playBundledPersianAudio("/audio/breeding/spawn-record.mp3");
          }}
        >
          <span aria-hidden="true">🥚</span>
          <strong>ثبت تخم‌ریزی</strong>
        </button>

        <button
          type="button"
          className="quick-button"
          disabled={!selectedPair}
          onClick={() => {
            setManagementAction("pair_exit");
            void playBundledPersianAudio("/audio/breeding/pair-exit.mp3");
          }}
        >
          <span aria-hidden="true">🚪</span>
          <strong>خروج جفت از مجموعه</strong>
        </button>

        <button
          type="button"
          className={
            lifecycleAction === "free_swim_record" ? "quick-button active" : "quick-button"
          }
          disabled={!lifecycleAllowed("free_swim_record")}
          onClick={() => {
            setLifecycleAction("free_swim_record");
            void playBundledPersianAudio("/audio/breeding/free-swim.mp3");
          }}
        >
          <span aria-hidden="true">🌀</span>
          <strong>ثبت شنای آزاد</strong>
        </button>

        <button
          type="button"
          className={
            lifecycleAction === "egg_result_record" ? "quick-button active" : "quick-button"
          }
          disabled={!lifecycleAllowed("egg_result_record")}
          onClick={() => {
            setLifecycleAction("egg_result_record");
            void playBundledPersianAudio("/audio/breeding/egg-result.mp3");
          }}
        >
          <span aria-hidden="true">🐣</span>
          <strong>نتیجه تخم‌ریزی</strong>
        </button>

        <button
          type="button"
          className={
            lifecycleAction === "parent_feeding_record" ? "quick-button active" : "quick-button"
          }
          disabled={!lifecycleAllowed("parent_feeding_record")}
          onClick={() => {
            setLifecycleAction("parent_feeding_record");
            void playBundledPersianAudio("/audio/breeding/parent-fry.mp3");
          }}
        >
          <span aria-hidden="true">👶</span>
          <strong>بچه‌های روی مولد</strong>
        </button>

        <button
          type="button"
          className={lifecycleAction === separationAction ? "quick-button active" : "quick-button"}
          disabled={!separationAction}
          onClick={() => {
            if (!separationAction) return;
            setLifecycleAction(separationAction);
            void playBundledPersianAudio("/audio/breeding/separation-transfer.mp3");
          }}
        >
          <span aria-hidden="true">🐟</span>
          <strong>جداسازی و انتقال</strong>
        </button>
      </div>

      {lifecycleAction && lifecycleTank ? (
        <BreedingActionPanel
          farmId={farmId}
          userId={userId}
          tank={lifecycleTank}
          action={lifecycleAction}
          {...(onSyncSummary ? { onSyncSummary } : {})}
          onCompleted={() => {
            setLifecycleAction(undefined);
            void refresh();
          }}
        />
      ) : null}

      <button type="button" className="text-button" onClick={() => void refresh()}>
        به‌روزرسانی
      </button>
    </section>
  );
}
